import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import { LoginRequest } from '../lib/tenant-api';
import { Eye, EyeOff } from 'lucide-react';
import { useTenantBrandingFromUrl } from '../hooks/useTenantBrandingFromUrl';

export default function Login() {
  const { login, isLoading } = useTenantAuth();
  const { hasSubdomain, tenantName, canonicalSubdomain } = useTenantBrandingFromUrl();
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
    tenant_subdomain: '',
  });
  const [error, setError] = useState<string>('');
  const [showSubdomainField, setShowSubdomainField] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (canonicalSubdomain) {
      setShowSubdomainField(false);
      setFormData(prev => ({
        ...prev,
        tenant_subdomain: canonicalSubdomain,
      }));
    }
  }, [canonicalSubdomain]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(formData);
      // Login successful - redirect will be handled by App component
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {tenantName
              ? `${tenantName} Admin Login`
              : hasSubdomain
                ? 'Admin Login'
                : 'Tenant Admin Login'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to manage your AI assistant content
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm: sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className=" bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {error}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            {showSubdomainField && (
              <div>
                <label htmlFor="tenant_subdomain" className="block text-sm font-medium text-gray-700">
                  Organization Subdomain
                </label>
                <div className="mt-1">
                  <input
                    id="tenant_subdomain"
                    name="tenant_subdomain"
                    type="text"
                    required
                    value={formData.tenant_subdomain}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300  placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="your-organization"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter your organization's subdomain (e.g., if your URL is acme.lawmabase.com, enter "acme")
                </p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300  placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300  placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent  shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}
